/**
 * Shared utility to send app settings telemetry to the backend.
 * Used on View mount (startup) and from SettingsModal on Continue.
 * Does not block on GPS or PIN; optional overrides can be passed for SettingsModal flow.
 */
import { useAuthStore } from '@renderer/stores/authStore'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'
import { usePrintStore } from '@renderer/stores/printStore'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useOfflineStore } from '@renderer/stores/offlineStore'
import { hasElectronPreload, isInTauriShell } from '@renderer/lib/runtime'
import { usePaperCounterStore } from '@renderer/stores/paperCounterStore'
import { useConfigStore } from '@renderer/stores/configState'

type SyncEngineState = 'idle' | 'syncing' | 'retrying' | 'paused'

function deriveTelemetrySyncStatus(
  state: SyncEngineState,
  stats: { pending: number; processing: number; failed: number; total: number }
): string {
  // Prefer engine state over queue counts when active or user-paused
  if (state === 'paused') return 'paused'
  if (state === 'syncing' || state === 'retrying') return 'syncing'
  if (stats.failed > 0) return 'failed'
  if (stats.pending > 0 || stats.processing > 0) return 'pending'
  return 'synced'
}

export interface SendAppSettingsTelemetryOptions {
  projectId?: string | null
  latitude?: number
  longitude?: number
  pin?: string
  pinStrength?: 'weak' | 'strong'
}

export async function sendAppSettingsTelemetry(
  options: SendAppSettingsTelemetryOptions = {}
): Promise<void> {
  const { projectId, latitude, longitude, pin, pinStrength } = options
  const userEmail = useAuthStore.getState().user?.email ?? ''
  const settingsStore = useSettingsStore.getState()
  const deviceId = settingsStore.deviceId
  if (!deviceId) {
    console.warn('[TELEMETRY] Missing deviceId for telemetry')
    return
  }

  const subscriptionStore = useSubscriptionStore.getState()
  const creditsStore = useCreditsStore.getState()
  const printStore = usePrintStore.getState()
  const sessionStore = useSessionStateStore.getState()
  const cameraStore = useCameraStore.getState()
  const appVersion = useOfflineStore.getState().appVersion ?? 'unknown'
  const paperStore = usePaperCounterStore.getState()
  const projectConfig = useConfigStore.getState().config
  const postSessionAi = projectConfig?.pages?.result?.settings?.postSessionAi

  let selectedCameraLabel: string | null = null

  // Collect system metrics and peripheral status via IPC (graceful fallback)
  let systemMetrics = { cpuUsagePercent: null as number | null, ramUsagePercent: null as number | null, diskFreeGB: null as number | null, diskDirectory: null as string | null, internetStatus: null as string | null }
  let peripheralStatus = { camera: null as string | null, printer: null as string | null, monitor: null as string | null }
  try {
    if (window.api?.getTelemetrySystemMetrics) {
      systemMetrics = await window.api.getTelemetrySystemMetrics()
    }
  } catch (err) {
    console.warn('[TELEMETRY] Failed to get system metrics:', err)
  }
  try {
    if (window.api?.getTelemetryPeripheralStatus) {
      const selectedPrinter = printStore.selectedPrinter || settingsStore.selectedPrinter || undefined
      peripheralStatus = await window.api.getTelemetryPeripheralStatus(selectedPrinter)
    }
  } catch (err) {
    console.warn('[TELEMETRY] Failed to get peripheral status:', err)
  }

  // One enumerateDevices pass: camera label + webcam fallback when DSLR path isn't "ready"
  const selectedCamId = settingsStore.selectedCamera
  if (selectedCamId || peripheralStatus.camera !== 'ready') {
    try {
      const mediaDevices = await navigator.mediaDevices.enumerateDevices()
      if (selectedCamId) {
        const match = mediaDevices.find(
          (d) => d.kind === 'videoinput' && d.deviceId === selectedCamId
        )
        if (match?.label) {
          selectedCameraLabel = match.label
        }
      }
      if (peripheralStatus.camera !== 'ready') {
        const webcamCount = mediaDevices.filter((d) => d.kind === 'videoinput').length
        if (webcamCount > 0) peripheralStatus.camera = 'ready'
      }
    } catch {
      // ignore — label stays null; keep existing camera status
    }
  }

  // Get internet status from existing IPC if system metrics didn't provide it
  if (systemMetrics.internetStatus == null) {
    try {
      if (window.api?.getInternetStatus) {
        const isConnected = await window.api.getInternetStatus()
        systemMetrics.internetStatus = isConnected ? 'connected' : 'disconnected'
      }
    } catch {
      // ignore
    }
  }

  let syncStatus: string | undefined
  try {
    if (window.api?.getSyncState && window.api?.getSyncStats) {
      const [state, stats] = await Promise.all([
        window.api.getSyncState(),
        window.api.getSyncStats()
      ])
      syncStatus = deriveTelemetrySyncStatus(state, stats)
    }
  } catch (err) {
    console.warn('[TELEMETRY] Failed to get sync status:', err)
  }

  const telemetry = {
    userId: userEmail || undefined,
    deviceId,
    appVersion,
    platform: hasElectronPreload() ? 'electron' : isInTauriShell() ? 'tauri' : 'web',
    subscription: {
      package: creditsStore.package || subscriptionStore.type || 'free',
      credits: creditsStore.credits,
      isValid: subscriptionStore.isValid || false,
      token: settingsStore.activationCode || undefined
    },
    features: {
      ejectCameraWhenIdle: settingsStore.ejectCameraWhenIdle ?? false,
      autoStart: settingsStore.autoStart ?? false,
      autoLaunch: settingsStore.autoLaunch ?? false,
      downloadLocal: settingsStore.downloadLocal ?? false,
      isPrintingEnabled: printStore.isPrintingEnabled ?? false
    },
    camera: {
      selectedCamera: settingsStore.selectedCamera || null,
      selectedCameraLabel: selectedCameraLabel,
      deviceType: cameraStore.deviceType || null,
      useShutterCapture: settingsStore.cameraSettings?.useShutterCapture ?? false,
      captureQuality: {
        maxWidth: settingsStore.cameraSettings?.captureQuality?.maxWidth || 1920,
        maxHeight: settingsStore.cameraSettings?.captureQuality?.maxHeight || 1080,
        quality: settingsStore.cameraSettings?.captureQuality?.quality || 1.0
      },
      previewQuality: settingsStore.cameraSettings?.previewQuality
        ? {
          maxWidth: settingsStore.cameraSettings.previewQuality.maxWidth || 1280,
          maxHeight: settingsStore.cameraSettings.previewQuality.maxHeight || 720,
          quality: settingsStore.cameraSettings.previewQuality.quality || 0.8,
          resizeQuality:
            settingsStore.cameraSettings.previewQuality.resizeQuality || 'medium'
        }
        : {
          maxWidth: 1280,
          maxHeight: 720,
          quality: 0.8,
          resizeQuality: 'medium' as const
        },
      captureRatio: settingsStore.cameraSettings?.captureRatio ?? undefined,
      mirror: settingsStore.cameraSettings?.mirror ?? false,
      applyFilterOnPreview: settingsStore.cameraSettings?.applyFilterOnPreview ?? false,
      previewRotation: settingsStore.cameraSettings?.previewRotation ?? 0,
      captureRotation: settingsStore.cameraSettings?.captureRotation ?? 0,
      selectedSecondaryCamera: settingsStore.selectedSecondaryCamera || null,
      secondaryCameraSettings: settingsStore.secondaryCameraSettings
        ? {
          captureRatio: settingsStore.secondaryCameraSettings.captureRatio ?? undefined,
          mirror: settingsStore.secondaryCameraSettings.mirror ?? false,
          useShutterCapture:
            settingsStore.secondaryCameraSettings.useShutterCapture ?? false,
          applyFilterOnPreview:
            settingsStore.secondaryCameraSettings.applyFilterOnPreview ?? false,
          previewQuality: settingsStore.secondaryCameraSettings.previewQuality
            ? {
              maxWidth:
                settingsStore.secondaryCameraSettings.previewQuality.maxWidth || 1280,
              maxHeight:
                settingsStore.secondaryCameraSettings.previewQuality.maxHeight || 720,
              quality:
                settingsStore.secondaryCameraSettings.previewQuality.quality || 0.8,
              resizeQuality:
                settingsStore.secondaryCameraSettings.previewQuality.resizeQuality ||
                'medium'
            }
            : undefined,
          captureQuality: settingsStore.secondaryCameraSettings.captureQuality
            ? {
              maxWidth:
                settingsStore.secondaryCameraSettings.captureQuality.maxWidth || 1920,
              maxHeight:
                settingsStore.secondaryCameraSettings.captureQuality.maxHeight || 1080,
              quality:
                settingsStore.secondaryCameraSettings.captureQuality.quality || 1.0,
              resizeQuality:
                settingsStore.secondaryCameraSettings.captureQuality.resizeQuality ||
                'high'
            }
            : undefined,
          previewRotation: settingsStore.secondaryCameraSettings.previewRotation ?? 0,
          captureRotation: settingsStore.secondaryCameraSettings.captureRotation ?? 0
        }
        : undefined
    },
    printer: {
      selectedPrinter: printStore.selectedPrinter || settingsStore.selectedPrinter || null,
      printerTarget: printStore.selectedPrinterTarget || 'default',
      selectedRemotePrinter: printStore.getSelectedRemotePrinter()
        ? {
          printerName: printStore.getSelectedRemotePrinter()?.printerName,
          machineIdentifier: printStore.getSelectedRemotePrinter()?.machineIdentifier
        }
        : null,
      useCSharpPrinting: printStore.useCSharpPrinting ?? false,
      useSumatraPDF: printStore.useSumatraPDF ?? false,
      settings: {
        orientation: printStore.settings.orientation || 'landscape',
        pageSize: {
          height: printStore.settings.pageSize?.height || 105000,
          width: printStore.settings.pageSize?.width || 156100
        },
        margins: printStore.settings.margins ?? undefined,
        dpi: printStore.settings.dpi ?? undefined,
        scaleFactor: printStore.settings.scaleFactor ?? undefined,
        printBackground: printStore.settings.printBackground ?? undefined,
        printResolutionScale: printStore.settings.printResolutionScale ?? undefined
      },
      secondaryPrinter: printStore.secondaryPrinter ?? undefined,
      tertiaryPrinter: printStore.tertiaryPrinter ?? undefined
    },
    session: {
      hasActiveSession: !!sessionStore.id,
      mode: sessionStore.mode || null
    },
    ...(postSessionAi
      ? {
          postSessionAi: {
            enabled: postSessionAi.enabled === true,
            target: postSessionAi.target ?? null
          }
        }
      : {}),
    ...(pin && pinStrength ? { pin, pinStrength } : {}),
    ...(latitude !== undefined && longitude !== undefined ? { latitude, longitude } : {}),
    heartbeatAt: new Date().toISOString(),
    cpuUsagePercent: systemMetrics.cpuUsagePercent,
    ramUsagePercent: systemMetrics.ramUsagePercent,
    diskFreeGB: systemMetrics.diskFreeGB,
    diskDirectory: systemMetrics.diskDirectory,
    internetStatus: systemMetrics.internetStatus,
    ...(syncStatus !== undefined ? { syncStatus } : {}),
    cameraStatus: peripheralStatus.camera,
    printerStatus: peripheralStatus.printer,
    monitorStatus: peripheralStatus.monitor,
    authStatus: userEmail ? 'logged_in' : 'logged_out',
    totalPaperUsed: paperStore.totalPaperUsed
  }

  try {
    const response = await fetch(`${process.env.VITE_API_URL}/api/app-settings-telemetry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        telemetry,
        projectId: projectId ?? null
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.warn('[TELEMETRY] Telemetry failed:', response.status, errorText)
      return
    }

    try {
      const responseData = await response.json()
      if (responseData.success && responseData.telemetryId) {
        useSessionStateStore.getState().setSessionState({
          appSettingsTelemetryId: responseData.telemetryId
        })
      }
    } catch (parseError) {
      console.warn('[TELEMETRY] Failed to parse telemetry response:', parseError)
    }
  } catch (err) {
    console.warn('[TELEMETRY] Heartbeat send failed:', err)
  }
}
